import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Code as ConnectCode, ConnectError } from '@connectrpc/connect';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { Code, Edit, FileText, Image, Loader2, Presentation, Search, Table, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useOfficeDocumentDrawer } from '../../contexts/OfficeDocumentDrawerContext';
import { useArtifactDrawer } from '../../hooks/useArtifactDrawer';
import { useArtifactSync } from '../../hooks/useArtifactSync';
import { useArtifacts } from '../../hooks/useArtifacts';
import { useDeleteDocument } from '../../hooks/useDeleteDocument';
import { useOfficeArtifacts } from '../../hooks/useOfficeArtifacts';
import { useChatArtifactStore } from '../../lib/stores/ChatArtifactStore';
// A human sentence for a failed delete, instead of the raw Connect error
// (`[permission_denied] denied`). The dialog renders this.
const deleteErrorMessage = (err)=>{
    if (err instanceof ConnectError) {
        switch(err.code){
            case ConnectCode.PermissionDenied:
                return "You don't have permission to delete this artifact.";
            case ConnectCode.NotFound:
                return 'This artifact no longer exists.';
            default:
                break;
        }
    }
    return "Couldn't delete the artifact. Please try again.";
};
const getArtifactIcon = (kind)=>{
    switch(kind){
        case 'code':
            return /*#__PURE__*/ _jsx(Code, {
                className: "w-6 h-6 text-blue-500"
            });
        case 'text':
            return /*#__PURE__*/ _jsx(FileText, {
                className: "w-6 h-6 text-gray-500"
            });
        case 'sheet':
            return /*#__PURE__*/ _jsx(Table, {
                className: "w-6 h-6 text-green-500"
            });
        case 'image':
            return /*#__PURE__*/ _jsx(Image, {
                className: "w-6 h-6 text-purple-500"
            });
        case 'presentation':
            return /*#__PURE__*/ _jsx(Presentation, {
                className: "w-6 h-6 text-orange-500"
            });
        default:
            return /*#__PURE__*/ _jsx(FileText, {
                className: "w-6 h-6 text-gray-500"
            });
    }
};
const formatDate = (date)=>{
    return `${date.toLocaleDateString()} ${date.toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit'
    })}`;
};
const getKindLabel = (kind)=>{
    const labels = {
        text: 'Text Document',
        code: 'Code Snippet',
        sheet: 'Spreadsheet',
        image: 'Image',
        presentation: 'Presentation',
        document: 'Document',
        powerpoint: 'PowerPoint'
    };
    return labels[kind] || kind;
};
// Office documents get a type-specific icon/label; everything else uses the
// chat-artifact kind.
const getRowIcon = (row)=>{
    switch(row.office){
        case 'docx':
            return /*#__PURE__*/ _jsx(FileText, {
                className: "w-6 h-6 text-blue-600"
            });
        case 'xlsx':
            return /*#__PURE__*/ _jsx(Table, {
                className: "w-6 h-6 text-green-600"
            });
        case 'pptx':
            return /*#__PURE__*/ _jsx(Presentation, {
                className: "w-6 h-6 text-orange-600"
            });
        default:
            return getArtifactIcon(row.kind);
    }
};
const getRowLabel = (row)=>{
    switch(row.office){
        case 'docx':
            return 'Word Document';
        case 'xlsx':
            return 'Spreadsheet';
        case 'pptx':
            return 'Presentation';
        default:
            return getKindLabel(row.kind);
    }
};
export default function ArtifactsView() {
    const [searchQuery, setSearchQuery] = useState('');
    const [filterKind, setFilterKind] = useState('all');
    const [sortBy, setSortBy] = useState('recent');
    const { openDrawer } = useArtifactDrawer();
    const { openOffice } = useOfficeDocumentDrawer();
    // Get store actions
    const selectArtifact = useChatArtifactStore((state)=>state.selectArtifact);
    const addArtifact = useChatArtifactStore((state)=>state.addArtifact);
    const getArtifact = useChatArtifactStore((state)=>state.getArtifact);
    const deleteFromStore = useChatArtifactStore((state)=>state.deleteArtifact);
    // Listen for cross-tab artifact changes (collapses an open drawer when another
    // tab edits/deletes the same artifact).
    useArtifactSync();
    // 'document' is our synthetic kind for Office docs, which come from shirohige
    // (useOfficeArtifacts), not the chat-artifact store — so don't ask the artifact
    // backend for it.
    const artifactKind = filterKind === 'all' || filterKind === 'document' ? undefined : filterKind;
    const { artifacts, loading, error, hasMore, refetch, removeArtifact } = useArtifacts({
        sortBy,
        kind: artifactKind,
        search: searchQuery || undefined
    });
    // Standalone Office documents (shirohige), owner-scoped, surfaced alongside the
    // chat artifacts. These open the OnlyOffice editor, not the inline drawer.
    const { officeDocs } = useOfficeArtifacts();
    const search = searchQuery.trim().toLowerCase();
    const officeRows = filterKind === 'all' || filterKind === 'document' ? officeDocs.filter((d)=>!search || d.title.toLowerCase().includes(search)).map((d)=>({
            id: d.id,
            title: d.title,
            kind: 'document',
            createdAt: d.createdAt,
            updatedAt: d.updatedAt,
            office: d.docType
        })) : [];
    const artifactRows = filterKind === 'document' ? [] : artifacts.map((a)=>({
            id: a.id,
            title: a.title,
            kind: a.kind,
            chatId: a.chatId,
            createdAt: a.createdAt,
            updatedAt: a.updatedAt
        }));
    const rows = [
        ...officeRows,
        ...artifactRows
    ].sort((a, b)=>sortBy === 'title' ? a.title.localeCompare(b.title) : b.updatedAt.getTime() - a.updatedAt.getTime());
    // Delete confirmation is a two-step flow: the row's trash button arms
    // `pendingDelete`, the dialog confirms it. Deletion is soft and owner-gated
    // server-side, so a non-owner's attempt surfaces as an error rather than
    // removing the row.
    const [pendingDelete, setPendingDelete] = useState(null);
    const deleteMutation = useDeleteDocument();
    // One mutation instance is shared by every row, so its error state must be
    // cleared whenever the dialog is (re)opened for a row — otherwise a failed
    // delete on one row shows its error preemptively on the next.
    const armDelete = (row)=>{
        deleteMutation.reset();
        setPendingDelete(row);
    };
    const closeDeleteDialog = ()=>{
        setPendingDelete(null);
        deleteMutation.reset();
    };
    const handleConfirmDelete = ()=>{
        if (!pendingDelete) return;
        const { id } = pendingDelete;
        deleteMutation.mutate(id, {
            onSuccess: ()=>{
                deleteFromStore(id); // collapse an open drawer on this artifact
                removeArtifact(id); // drop the row (delete is confirmed + durable)
                setPendingDelete(null);
                // Optimistic removal is authoritative for the current page. When there
                // are MORE pages (hasMore), refetch so the vacated slot backfills from
                // the next page and total/hasMore stay accurate — the common case (a
                // single 50-row page) skips it, avoiding a needless round-trip.
                if (hasMore) refetch();
            }
        });
    };
    const handleEditClick = (row)=>{
        // Office documents live in shirohige (content in the document server), not
        // the string-content artifact store, so they open the OnlyOffice editor.
        if (row.office) {
            openOffice(row.id, {
                title: row.title,
                docType: row.office
            });
            return;
        }
        // Only add artifact to store if it doesn't exist OR has no content
        // This prevents overwriting an artifact that already has fetched content
        const existingArtifact = getArtifact(row.id);
        if (!existingArtifact || !existingArtifact.content) {
            addArtifact({
                id: row.id,
                title: row.title,
                kind: row.kind,
                content: '',
                chatId: row.chatId,
                createdAt: row.createdAt.toISOString(),
                updatedAt: row.updatedAt.toISOString()
            });
        }
        selectArtifact(row.id);
        openDrawer(row.id, {
            sourceContext: 'artifacts-page',
            chatId: row.chatId
        });
    };
    if (loading) {
        return /*#__PURE__*/ _jsx("div", {
            className: "flex items-center justify-center h-96",
            children: /*#__PURE__*/ _jsxs("div", {
                className: "flex flex-col items-center space-y-3",
                children: [
                    /*#__PURE__*/ _jsx(Loader2, {
                        className: "w-8 h-8 animate-spin text-indigo-600"
                    }),
                    /*#__PURE__*/ _jsx("p", {
                        className: "text-sm text-gray-500 dark:text-gray-400",
                        children: "Loading artifacts..."
                    })
                ]
            })
        });
    }
    if (error) {
        return /*#__PURE__*/ _jsxs("div", {
            className: "bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4",
            children: [
                /*#__PURE__*/ _jsx("h3", {
                    className: "font-medium text-red-900 dark:text-red-200",
                    children: "Error loading artifacts"
                }),
                /*#__PURE__*/ _jsx("p", {
                    className: "text-sm text-red-700 dark:text-red-300 mt-1",
                    children: error
                })
            ]
        });
    }
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-6",
        children: [
            /*#__PURE__*/ _jsxs("div", {
                children: [
                    /*#__PURE__*/ _jsx("h2", {
                        className: "text-2xl font-bold text-gray-900 dark:text-white",
                        children: "Artifacts"
                    }),
                    /*#__PURE__*/ _jsxs("p", {
                        className: "text-sm text-gray-500 dark:text-gray-400 mt-1",
                        children: [
                            rows.length,
                            " ",
                            rows.length === 1 ? 'item' : 'items',
                            " — your documents and artifacts"
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6",
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex flex-col sm:flex-row gap-4",
                        children: [
                            /*#__PURE__*/ _jsxs("div", {
                                className: "relative flex-1",
                                children: [
                                    /*#__PURE__*/ _jsx(Search, {
                                        className: "absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4"
                                    }),
                                    /*#__PURE__*/ _jsx("input", {
                                        type: "text",
                                        placeholder: "Search artifacts by title...",
                                        value: searchQuery,
                                        onChange: (e)=>setSearchQuery(e.target.value),
                                        className: "pl-10 pr-4 py-2 w-full border border-gray-300 dark:border-gray-600 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsxs("select", {
                                value: filterKind,
                                onChange: (e)=>setFilterKind(e.target.value),
                                className: "px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white",
                                children: [
                                    /*#__PURE__*/ _jsx("option", {
                                        value: "all",
                                        children: "All Types"
                                    }),
                                    /*#__PURE__*/ _jsx("option", {
                                        value: "text",
                                        children: "Text Document"
                                    }),
                                    /*#__PURE__*/ _jsx("option", {
                                        value: "code",
                                        children: "Code Snippet"
                                    }),
                                    /*#__PURE__*/ _jsx("option", {
                                        value: "sheet",
                                        children: "Spreadsheet"
                                    }),
                                    /*#__PURE__*/ _jsx("option", {
                                        value: "image",
                                        children: "Image"
                                    }),
                                    /*#__PURE__*/ _jsx("option", {
                                        value: "presentation",
                                        children: "Presentation"
                                    }),
                                    /*#__PURE__*/ _jsx("option", {
                                        value: "document",
                                        children: "Documents"
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsxs("select", {
                                value: sortBy,
                                onChange: (e)=>setSortBy(e.target.value),
                                className: "px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white",
                                children: [
                                    /*#__PURE__*/ _jsx("option", {
                                        value: "recent",
                                        children: "Most Recent"
                                    }),
                                    /*#__PURE__*/ _jsx("option", {
                                        value: "title",
                                        children: "Title A-Z"
                                    })
                                ]
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        className: "mt-4 text-sm text-gray-600 dark:text-gray-400",
                        children: /*#__PURE__*/ _jsxs("span", {
                            children: [
                                "Showing: ",
                                /*#__PURE__*/ _jsx("strong", {
                                    className: "text-gray-900 dark:text-white",
                                    children: rows.length
                                })
                            ]
                        })
                    })
                ]
            }),
            rows.length === 0 ? /*#__PURE__*/ _jsxs("div", {
                className: "text-center py-12 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700",
                children: [
                    /*#__PURE__*/ _jsx(FileText, {
                        className: "mx-auto h-12 w-12 text-gray-400"
                    }),
                    /*#__PURE__*/ _jsx("h3", {
                        className: "mt-2 text-sm font-medium text-gray-900 dark:text-white",
                        children: "No artifacts found"
                    }),
                    /*#__PURE__*/ _jsx("p", {
                        className: "mt-1 text-sm text-gray-500 dark:text-gray-400",
                        children: searchQuery || filterKind !== 'all' ? 'Try adjusting your search or filter criteria.' : 'Create your first artifact in a chat conversation.'
                    })
                ]
            }) : /*#__PURE__*/ _jsx("div", {
                className: "bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden",
                children: /*#__PURE__*/ _jsx("div", {
                    className: "overflow-x-auto",
                    children: /*#__PURE__*/ _jsxs("table", {
                        className: "min-w-full divide-y divide-gray-200 dark:divide-gray-700",
                        children: [
                            /*#__PURE__*/ _jsx("thead", {
                                className: "bg-gray-50 dark:bg-gray-900/50",
                                children: /*#__PURE__*/ _jsxs("tr", {
                                    children: [
                                        /*#__PURE__*/ _jsx("th", {
                                            className: "px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider",
                                            children: "Title"
                                        }),
                                        /*#__PURE__*/ _jsx("th", {
                                            className: "px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider",
                                            children: "Type"
                                        }),
                                        /*#__PURE__*/ _jsx("th", {
                                            className: "px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider",
                                            children: "Created"
                                        }),
                                        /*#__PURE__*/ _jsx("th", {
                                            className: "px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider",
                                            children: "Last Updated"
                                        }),
                                        /*#__PURE__*/ _jsx("th", {
                                            className: "px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider",
                                            children: "Actions"
                                        })
                                    ]
                                })
                            }),
                            /*#__PURE__*/ _jsx("tbody", {
                                className: "bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700",
                                children: rows.map((row)=>/*#__PURE__*/ _jsxs("tr", {
                                        className: "hover:bg-gray-50 dark:hover:bg-gray-700/50",
                                        children: [
                                            /*#__PURE__*/ _jsx("td", {
                                                className: "px-6 py-4",
                                                children: /*#__PURE__*/ _jsxs("div", {
                                                    className: "flex items-center",
                                                    children: [
                                                        getRowIcon(row),
                                                        /*#__PURE__*/ _jsx("div", {
                                                            className: "ml-3",
                                                            children: /*#__PURE__*/ _jsx("div", {
                                                                className: "text-sm font-medium text-gray-900 dark:text-white",
                                                                children: row.title
                                                            })
                                                        })
                                                    ]
                                                })
                                            }),
                                            /*#__PURE__*/ _jsx("td", {
                                                className: "px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400",
                                                children: getRowLabel(row)
                                            }),
                                            /*#__PURE__*/ _jsx("td", {
                                                className: "px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400",
                                                children: formatDate(row.createdAt)
                                            }),
                                            /*#__PURE__*/ _jsx("td", {
                                                className: "px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400",
                                                children: formatDate(row.updatedAt)
                                            }),
                                            /*#__PURE__*/ _jsx("td", {
                                                className: "px-6 py-4 whitespace-nowrap text-right text-sm font-medium",
                                                children: /*#__PURE__*/ _jsxs("div", {
                                                    className: "inline-flex items-center gap-4",
                                                    children: [
                                                        /*#__PURE__*/ _jsxs("button", {
                                                            onClick: ()=>handleEditClick(row),
                                                            "data-testid": Testids.artifacts.rowOpenButton,
                                                            className: "inline-flex items-center text-indigo-600 hover:text-indigo-900 dark:text-indigo-400 dark:hover:text-indigo-300",
                                                            title: row.office ? 'Open document' : 'Edit artifact',
                                                            "aria-label": row.office ? 'Open document' : 'Edit artifact',
                                                            children: [
                                                                /*#__PURE__*/ _jsx(Edit, {
                                                                    className: "w-4 h-4 mr-1"
                                                                }),
                                                                row.office ? 'Open' : 'Edit'
                                                            ]
                                                        }),
                                                        !row.office && /*#__PURE__*/ _jsxs("button", {
                                                            onClick: ()=>armDelete(row),
                                                            className: "inline-flex items-center text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300",
                                                            title: "Delete artifact",
                                                            "aria-label": "Delete artifact",
                                                            children: [
                                                                /*#__PURE__*/ _jsx(Trash2, {
                                                                    className: "w-4 h-4 mr-1"
                                                                }),
                                                                "Delete"
                                                            ]
                                                        })
                                                    ]
                                                })
                                            })
                                        ]
                                    }, row.id))
                            })
                        ]
                    })
                })
            }),
            /*#__PURE__*/ _jsx(AlertDialog, {
                open: pendingDelete !== null,
                onOpenChange: (open)=>{
                    if (!open && !deleteMutation.isPending) closeDeleteDialog();
                },
                children: /*#__PURE__*/ _jsxs(AlertDialogContent, {
                    children: [
                        /*#__PURE__*/ _jsxs(AlertDialogHeader, {
                            children: [
                                /*#__PURE__*/ _jsx(AlertDialogTitle, {
                                    children: "Delete artifact?"
                                }),
                                /*#__PURE__*/ _jsx(AlertDialogDescription, {
                                    children: pendingDelete ? `"${pendingDelete.title || 'Untitled'}" and its version history will be removed from your artifacts. This can't be undone here.` : ''
                                })
                            ]
                        }),
                        deleteMutation.isError && /*#__PURE__*/ _jsx("p", {
                            className: "text-sm text-red-600 dark:text-red-400",
                            children: deleteErrorMessage(deleteMutation.error)
                        }),
                        /*#__PURE__*/ _jsxs(AlertDialogFooter, {
                            children: [
                                /*#__PURE__*/ _jsx(AlertDialogCancel, {
                                    disabled: deleteMutation.isPending,
                                    children: "Cancel"
                                }),
                                /*#__PURE__*/ _jsx(AlertDialogAction, {
                                    onClick: (e)=>{
                                        // Keep the dialog open until the mutation resolves.
                                        e.preventDefault();
                                        handleConfirmDelete();
                                    },
                                    disabled: deleteMutation.isPending,
                                    "aria-label": "Confirm delete artifact",
                                    children: deleteMutation.isPending ? 'Deleting…' : 'Delete'
                                })
                            ]
                        })
                    ]
                })
            })
        ]
    });
}
